import { observable, action, computed, isObservable } from 'mobx';
import { debounce } from 'throttle-debounce';

import { unObserve } from 'core/utils';

import { AppManager, Variables } from 'core/index';
import { client } from 'core/client';

import { env } from 'process';

import { createNotification } from 'apps/utils';

import { clientStore } from 'apps/client/app';

import { InboxSubscription, CONVERSATIONS_CONNECTION_QUERY } from './gql';
import { ConversationsApp, ConversationModel } from './app';

export class ConversationsConnManager extends AppManager {
  @observable pageNum = 0;
  @observable nodes = [];
  @observable total = 0;
  @observable hasDataBeenLoaded = false;
  @observable loading = false;

  constructor(parent, init, options) {
    super(parent, init, options);
    this.batchSize = 10;
    this.initialBatchSize = 15;

    this.variables = this.variables.extend(
      new Variables({
        // spaceId: () => clientStore.activeSpaceId,

        // offset: () => this.pageNum * this.batchSize,
        // first: () => this.batchSize,
        offset: () => 0,
        first: () => this.initialBatchSize + this.batchSize * this.pageNum,
      }),
    );
  }

  process = queryResult => {
    const Model = ConversationModel;
    const result = {};
    if (queryResult) {
      result.nodes = queryResult.data.conversations.nodes.map(node =>
        Model.Load(node),
      );
      result.total = queryResult.data.conversations.total;
    }
    return result;
  };

  // process = queryResult => {
  //   const Model = ConversationModel;
  //   if (queryResult) {
  //     queryResult.nodes = queryResult.nodes.map(node => Model.Load(node));
  //   }
  //   return queryResult;
  // };

  @action SetConversations = res => {
    this.loading = false;
    if (res) {
      this.hasDataBeenLoaded = true;
      this.total = res.total;

      if (this.nodes.length <= res.nodes.length) {
        this.nodes = res.nodes;
      } else {
        // If we got a smaller set of data than what we currently have, put the new data at the start
        // and remove the conversations from the previous data that have an ID of something we just got.
        const newDataIds = {};
        res.nodes.forEach(element => {
          newDataIds[element.id] = true;
        });
        const previousData = this.nodes.filter(
          conversation => !newDataIds[conversation.id],
        );
        this.nodes = [...res.nodes, ...previousData];
      }
    }
  };

  queryFromStart = () => {
    this.loading = true;
    this.pageNum = 0;

    const context = {
      abortOnRerequest: true,
      queryDeduplication: false,
    };
    const GQL = CONVERSATIONS_CONNECTION_QUERY;
    client
      .query({
        query: GQL,
        variables: isObservable(this.variables)
          ? unObserve(this.variables, ['offset', 'first'])
          : this.variables,
        fetchPolicy: 'network-only', // 'cache-first'
        context,
      })
      .then(this.process)
      .then(this.SetConversations)
      .catch(error => {
        this.loading = false;
        if (env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log(
            'CONVERSATIONS_CONNECTION_QUERY.queryFromStart error',
            error,
          );
        }
      });
  };

  fetchMore = () => {
    this.loading = true;
    this.pageNum += 1;

    const context = {
      abortOnRerequest: true,
      queryDeduplication: false,
    };

    client
      .query({
        query: CONVERSATIONS_CONNECTION_QUERY,
        variables: isObservable(this.variables)
          ? unObserve(this.variables, ['offset', 'first'])
          : this.variables,
        fetchPolicy: 'network-only', // 'cache-first'
        context,
      })

      .then(this.process)
      .then(this.SetConversations)
      .catch(error => {
        this.loading = false;
        if (env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log('CONVERSATIONS_CONNECTION_QUERY.fetchMore error', error);
        }
      });
  };

  // queryFromStart = () => {
  //   this.pageNum = 0;
  //   // this.initialiseQuery(ConversationsConnectionQuery, this.variables);
  //   if (this.query) {
  //     this.query.disposer();
  //   }

  //   this.query = new ConversationsConnectionQuery(this, {
  //     variables: this.variables,
  //     onSysErr: this.PageLoadErr,
  //     abortOnRerequest: true,
  //   });
  //   this.query.process(this.process, this.SetConversations);
  //   this.query.query();
  // };

  // fetchMore = () => {
  //   this.pageNum += 1;

  //   if (this.moreQuery) {
  //     this.moreQuery.disposer();
  //   }
  //   this.moreQuery = new ConversationsConnectionQuery(this, {
  //     variables: this.variables,
  //     onSysErr: this.PageLoadErr,
  //     abortOnRerequest: true,
  //   });
  //   this.moreQuery.process(this.process, this.SetConversations);
  //   // for some bizarre reason this triggers two requests when used with moreQuery de-duplication 'false'
  //   this.moreQuery.query();
  // };
}

const INBOX_QUERY_DEBOUNCE_MS = 10000;

export default class InboxAppManager extends AppManager {
  static registryName = 'InboxAppManager';

  @observable conversationsConnManager = null;
  @observable totalUnread = 0;
  @observable total = 0;

  @observable isInboxOpen = false;

  lastTimeInboxWasQueried = 0;
  unreadCount = 0;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('conversations', ConversationsApp);

    // this.variables = new Variables({});
    this.conversationsConnManager = new ConversationsConnManager(this, {
      // initialBatchSize
      // batchSize
      // sortBy
      // loadMoreQuery
    });
  }

  formatMessage = count => {
    if (count > 1) {
      return `You have ${count} unread messages`;
    }

    return 'You have an unread message';
  };

  createNotificationDebounced = () =>
    debounce(1200, async id => {
      await createNotification({
        title: this.formatMessage(this.unreadCount),
        notificationId: id,
        options: {
          tag: id,
        },
        tabId: clientStore.currentTabId,
      });
      this.unreadCount = 0;
    });

  @computed get loading() {
    return this.conversationsConnManager.loading;
  }

  @computed get areThereMoreItemsToFetch() {
    return (
      !this.conversationsConnManager.hasDataBeenLoaded ||
      this.conversationsConnManager.nodes.length <
        this.conversationsConnManager.total
    );
  }

  @action QueryInbox() {
    this.lastTimeInboxWasQueried = Date.now();
    this.conversationsConnManager.queryFromStart();
  }

  @action ToggleInbox = forceOpenState => {
    // if it's opening maybe query
    if (!this.isInboxOpen && forceOpenState !== false) this.MaybeQueryInbox();

    if (typeof forceOpenState === 'boolean') this.isInboxOpen = forceOpenState;
    else this.isInboxOpen = !this.isInboxOpen;
  };

  @computed get isSubscribed() {
    return !!this.inboxSubscription;
  }

  @action MaybeQueryInbox = () => {
    if (Date.now() - this.lastTimeInboxWasQueried < INBOX_QUERY_DEBOUNCE_MS)
      return;
    this.QueryInbox();
  };

  @action SubscribeInbox = () => {
    this.inboxSubscription = new InboxSubscription(this, { variables: {} });

    // this is the initial Inbox call triggered by frameStore setting up polling when user signs in
    this.MaybeQueryInbox();

    this.inboxSubscription.StartSubscription();

    const debouncedNotification = this.createNotificationDebounced();

    this.inboxSubscription.process(result => {
      if (!result) return null;
      this.unreadCount += 1;
      debouncedNotification(result.id);
      this.QueryInbox();
    });
  };

  @computed get notificationBadgeNum() {
    // return this.totalUnread;

    // NOTE(david): This whole block is a local hack due to missing server data.
    // the condition " && conversation.lastMessage" is a further hack because such
    // conversations will get skipped by the Inbox, even though they should not exist.
    return this.conversationsConnManager.nodes.reduce((acc, conversation) => {
      if (conversation.unreadCount > 0 && conversation.lastMessage)
        return acc + 1;
      return acc;
    }, 0);
  }

  @action StopSubscribeInbox = () => {
    if (this.inboxSubscription) {
      this.inboxSubscription.CompleteSubscription();
      this.inboxSubscription = null;
    }
  };
}
